import { useMemo, useState } from "react";
import { ArrowRight, CalendarDays, Clock3, House, Info, MessageCircle, Timer, User } from "lucide-react";
import { Reveal } from "./Reveal";
import { WA_NUMBER, dayName, formatRupiah, minBookingDate, staffRecommendation } from "../data";

const Field = ({ num, label, children }) => (
  <div className="f-group">
    <span className="f-label">
      {num && <span className="f-num">{num}</span>}
      {label}
    </span>
    {children}
  </div>
);

export default function Booking() {
  const [service, setService] = useState("Reguler Cleaning");
  const [gender, setGender] = useState("Wanita");
  const [staff, setStaff] = useState(1);
  const [duration, setDuration] = useState(3);
  const [date, setDate] = useState("");
  const [area, setArea] = useState("");
  const [notes, setNotes] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [dateError, setDateError] = useState("");

  const rate = service === "Reguler Cleaning" ? 60000 : 75000;
  const total = rate * duration * staff;
  const staffRec = staffRecommendation(Number(area));
  const selectedDay = useMemo(() => dayName(date), [date]);

  const openWhatsApp = (survey = false) => {
    if (!survey && (!date || date < minBookingDate)) {
      setDateError(date ? "Pesanan minimal H-1: pilih tanggal besok atau setelahnya." : "Pilih tanggal layanan terlebih dahulu (minimal H-1).");
      return;
    }
    setDateError("");
    const message = survey
      ? "Halo Antar Cleaning, saya ingin Request Survey untuk hunian di atas 250m2. Mohon info jadwal survey dan detailnya."
      : `Halo Antar Cleaning, saya mau pesan:\n- Layanan: ${service}\n- Gender Petugas: ${gender}\n- Jumlah Petugas: ${staff} orang\n- Durasi: ${duration} Jam\n- Hari: ${selectedDay}\n- Luas: ${area || "Belum diisi"} m2\n- Total: ${formatRupiah(total)}\n- Catatan: ${notes || "-"}\n- Nama: ${name}\n- No WhatsApp: ${phone}\n- Alamat: ${address}`;
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="section booking" id="pesan">
      <div className="container">
        <Reveal>
          <div className="booking-card" data-testid="booking-form">
            <div className="bk-head">
              <div>
                <span className="eyebrow">Pesan Layanan</span>
                <h2 className="h2">
                  Rumah bersih,
                  <br />
                  <em>hati pun lega.</em>
                </h2>
              </div>
              <p>Isi detailnya, kami siapkan tim terbaik untuk Anda. Pesanan langsung terkirim ke WhatsApp kami.</p>
            </div>

            <div className="bk-grid">
              <Field num="1" label="Pilih layanan">
                <div className="choice-grid">
                  {[
                    { name: "Reguler Cleaning", sub: "Perawatan rutin", price: "60k" },
                    { name: "Deep Cleaning", sub: "Bersih menyeluruh", price: "75k" },
                  ].map((s) => (
                    <label className={`choice ${service === s.name ? "on" : ""}`} key={s.name} data-testid={`service-${s.name.split(" ")[0].toLowerCase()}-option`}>
                      <input type="radio" name="service" checked={service === s.name} onChange={() => setService(s.name)} />
                      <span className="choice-body">
                        <strong>{s.name}</strong>
                        <small>{s.sub}</small>
                      </span>
                      <b>
                        {s.price}
                        <small>/jam</small>
                      </b>
                    </label>
                  ))}
                </div>
              </Field>

              <Field num="2" label="Preferensi petugas">
                <div className="gender-grid">
                  {["Pria", "Wanita"].map((g) => (
                    <label className={`choice gender ${gender === g ? "on" : ""}`} key={g} data-testid={`gender-${g.toLowerCase()}-option`}>
                      <input type="radio" name="gender" checked={gender === g} onChange={() => setGender(g)} />
                      <span className="gender-ico">
                        <User size={15} />
                      </span>
                      {g}
                    </label>
                  ))}
                </div>
              </Field>

              <Field num="3" label="Jumlah petugas yang dibutuhkan">
                <div className="dur-row staff-row" data-testid="staff-count-field">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <button type="button" key={n} className={`dur ${staff === n ? "on" : ""}`} onClick={() => setStaff(n)} data-testid={`staff-${n}-button`}>
                      <b>{n}</b> org
                    </button>
                  ))}
                </div>
                <small className="staff-hint" data-testid="staff-recommendation">
                  {Number(area) > 250 ? (
                    "Luas di atas 250 m²: disarankan survey dahulu untuk menentukan jumlah petugas."
                  ) : staffRec ? (
                    <>
                      Rekomendasi untuk {area} m²:{" "}
                      <strong>
                        {staffRec.min}
                        {staffRec.max > staffRec.min ? `–${staffRec.max}` : ""} petugas
                      </strong>{" "}
                      (patokan ±50 m² per petugas, berlaku untuk Reguler & Deep Cleaning).
                    </>
                  ) : (
                    "Isi luas hunian untuk rekomendasi otomatis (patokan ±50 m² per petugas, berlaku untuk Reguler & Deep Cleaning)."
                  )}
                </small>
              </Field>

              <Field num="4" label="Durasi layanan">
                <div className="dur-row">
                  {[3, 4, 5, 6].map((d) => (
                    <button type="button" key={d} className={`dur ${duration === d ? "on" : ""}`} onClick={() => setDuration(d)} data-testid={`duration-${d}-button`}>
                      <b>{d}</b> jam
                    </button>
                  ))}
                </div>
              </Field>

              <div className="input-row">
                <Field num="5" label="Hari pesanan">
                  <div className="input-ico">
                    <CalendarDays size={16} />
                    <input
                      type="date"
                      min={minBookingDate}
                      value={date}
                      onChange={(e) => {
                        setDate(e.target.value);
                        setDateError("");
                      }}
                      data-testid="booking-date-input"
                    />
                  </div>
                  {dateError && (
                    <small className="date-error" data-testid="booking-date-error">
                      {dateError}
                    </small>
                  )}
                </Field>
                <Field num="6" label="Luas hunian (m²)">
                  <div className="input-ico">
                    <House size={16} />
                    <input
                      type="number"
                      min="1"
                      placeholder="Contoh: 120"
                      value={area}
                      onChange={(e) => {
                        setArea(e.target.value);
                        const r = staffRecommendation(Number(e.target.value));
                        if (r) setStaff(r.min);
                      }}
                      data-testid="booking-area-input"
                    />
                  </div>
                </Field>
              </div>

              <div className="notice-h1" data-testid="booking-notice">
                <Timer size={16} />
                <p>
                  <strong>Saran pemesanan:</strong> kami hanya melayani pesanan H-1 yang masuk sebelum jam 18.00 (operasional tutup). Pesan
                  lebih awal agar jadwal Anda aman.
                </p>
              </div>

              {Number(area) > 250 && (
                <div className="alert-250" data-testid="survey-alert">
                  <div>
                    <strong>Hunian di atas 250 m²?</strong>
                    <p>Disarankan survey terlebih dahulu agar hasil maksimal. Biaya survey Rp 65.000/visit.</p>
                  </div>
                  <button type="button" onClick={() => openWhatsApp(true)} data-testid="request-survey-button">
                    Request Survey <ArrowRight size={14} />
                  </button>
                </div>
              )}

              <Field num="7" label="Catatan layanan">
                <textarea
                  placeholder="Contoh: Tolong bawa alat pel sendiri, ada 2 kucing di rumah..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="booking-notes-input"
                />
              </Field>

              <div className="bk-divider">
                <span>Data diri</span>
              </div>

              <div className="input-row">
                <Field label="Nama lengkap">
                  <input placeholder="Nama Anda" value={name} onChange={(e) => setName(e.target.value)} data-testid="booking-name-input" />
                </Field>
                <Field label="No. WhatsApp">
                  <input type="tel" placeholder="08xxxxxxxxxx" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="booking-phone-input" />
                </Field>
              </div>
              <Field label="Alamat lengkap">
                <textarea placeholder="Alamat rumah yang akan dibersihkan" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="booking-address-input" />
              </Field>
            </div>

            <div className="bk-foot">
              <div className="total" data-testid="booking-summary">
                <small>ESTIMASI TOTAL</small>
                <strong>{formatRupiah(total)}</strong>
                <span>
                  <Clock3 size={13} /> {duration} jam · {staff} petugas · {formatRupiah(rate)}/jam/petugas
                </span>
              </div>
              <button type="button" className="btn-wa" onClick={() => openWhatsApp()} data-testid="whatsapp-booking-button">
                <MessageCircle size={18} /> Pesan via WhatsApp <ArrowRight size={16} />
              </button>
            </div>
            <p className="policy" data-testid="refund-policy-note">
              <Info size={14} /> Refund/reschedule hanya bisa dilakukan maksimal 2 jam sebelum layanan. Lewat dari waktu tersebut dikenakan Rp
              25.000 untuk biaya transport petugas.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
